import React, { useState } from 'react';
import { readMetaAttribution } from '@privisy/ui-bridge';
import { emailReport } from '../utils/api';
import { ApiError } from '../utils/api-error';
import { T } from '../designTokens';

interface EmailReportFormProps {
  scanId: string;
  /** The anonymous scan's intent token: the route's write credential. */
  intentToken: string;
}

/** Copy per typed error code (#1695): the form never shows the API's prose. */
function errorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.apiCode) {
      case 'INVALID_REQUEST':
        return 'Enter a valid email address and tick the box.';
      case 'REPORT_EMAIL_LIMIT':
        return "This report can't be emailed to any more addresses right now.";
      case 'EMAIL_SEND_FAILED':
        return "We couldn't send the email. Please try again.";
      case 'SCAN_ALREADY_CLAIMED':
        return 'This report is already in an account. Sign in to view it.';
      case 'INVALID_INTENT_TOKEN':
      case 'SCAN_NOT_FOUND':
        return "This report's link has expired, so we can't email it.";
    }
  }
  return 'Something went wrong. Please try again.';
}

type Status = 'idle' | 'sending' | 'sent' | 'error';

/**
 * "Email me this report" on the locked report (#1780). Optional and inline:
 * it never gates or hides the report. The consent box starts unticked and
 * the button stays disabled until both the address and the box are filled.
 */
export default function EmailReportForm({ scanId, intentToken }: EmailReportFormProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = email.trim() !== '' && consent && status !== 'sending';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('sending');
    setError(null);
    try {
      const { sent } = await emailReport(scanId, intentToken, email.trim(), readMetaAttribution());
      if (sent) {
        setStatus('sent');
      } else {
        setStatus('error');
        setError("We couldn't send the email right now. Please try again later.");
      }
    } catch (err) {
      setStatus('error');
      setError(errorCopy(err));
    }
  };

  if (status === 'sent') {
    return (
      <div role="status" style={{ margin: '24px 0 0', fontSize: '14px', color: T.emerald700 }}>
        Sent. Check your inbox for the link to this report.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Email me this report"
      style={{ margin: '24px 0 0', padding: '16px', border: `1px solid ${T.gray200}`, borderRadius: '8px' }}
    >
      <label htmlFor="email-report-address" style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: T.gray700, marginBottom: '8px' }}>
        Email me this report
      </label>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <input
          id="email-report-address"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{
            flex: '1 1 220px', padding: '8px 12px', fontSize: '14px',
            border: `1px solid ${T.gray300}`, borderRadius: '6px', color: T.gray900,
          }}
        />
        <button type="submit" disabled={!canSubmit} className="legacy-cta__button">
          {status === 'sending' ? 'Sending…' : 'Send'}
        </button>
      </div>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', fontSize: '12px', color: T.gray500, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ marginTop: '2px', flexShrink: 0 }}
        />
        <span>
          Send me a link to this report and occasional emails about fixing what it found. Unsubscribe anytime.{' '}
          <a
            href="https://getprivisy.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: T.cobalt600, textDecoration: 'underline' }}
          >
            Privacy policy
          </a>
        </span>
      </label>
      {error && (
        <p role="alert" style={{ margin: '10px 0 0', fontSize: '13px', color: T.red700 }}>
          {error}
        </p>
      )}
    </form>
  );
}
